import { getQuarter } from "@utils/dateUtils";
import { currencyFormatter } from "@utils/formatedPrice";
import { toUnicodeLocale } from "@utils/string";
import { useSession } from "next-auth/react";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { FC, useContext, useEffect, useRef, useState } from "react";

import { getAvailability } from "@/components/AvailableUnitsList/action";
import { useNotification } from "@/components/NotificationProvider/NotificationProvider";
import { NotificationType } from "@/components/NotificationProvider/type";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import AvailableUnitListProvider, { AvailableUnitContext } from "@/context/AvailableUnitListContext";
import { LoadingContext } from "@/context/LoaderContext";
import { NavigationContext } from "@/context/NavigationContext";
import { SelectUnitContext } from "@/context/SelectUnitContext";
import { UserContext } from "@/context/UserContext";
import { ctaClickTracking, ecomInitiateCheckout, ecomSelectReserve, ecomViewWishlist } from "@/dataLayer/dataLayer";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { CommercetoolsProject, PRODUCT_TYPE } from "@/types";

import CurrencyIcon from "../ui/CurrencyIcon/CurrencyIcon";

const CookieExpirationDate = new Date();
CookieExpirationDate.setFullYear(CookieExpirationDate.getFullYear() + 10);

export interface ShorlistPopupProps {
  items: CommercetoolsProject[];
  closeFunc: (state: boolean) => void;
}

const ShortlistPopup: FC<ShorlistPopupProps> = ({ items, closeFunc }) => {
  const t = useTranslations();
  const router = useRouter();
  const locale = useLocale();
  const [isLogged, setIsLogged] = useState(false);
  const { askLogin } = useNotification();
  const { favorites } = useNotification();
  const handleRegister = () => {
    closeFunc(false);
    askLogin();
  };

  const { data: session } = useSession();

  const [language, region] = locale.split("-");
  const isRTL = language === "ar";
  useEffect(() => {
    if (session?.user) {
      setIsLogged(true);
    }
  }, [session]);

  const containerRef = useRef<HTMLDivElement>(null);
  useOutsideClick(containerRef, () => {
    closeFunc(false);
  });

  return (
    <>
      <div
        ref={containerRef}
        className={`fixed z-[100] left-0  bottom-0 w-full md:top-[80px] ${isRTL ? "md:left-l" : "md:right-l"} ${isRTL ? "md:right-auto" : "md:left-auto"} md:bottom-auto bg-gray-10 rounded-tr-[30px] rounded-tl-[30px] md:rounded-lg md:w-[367px] p-m shadow-elevation`}
      >
        <div
          className="absolute right-m"
          onClick={() => {
            closeFunc(false);
          }}
        >
          <Icon size={"24"} name={"close"} className="stroke-black" />
        </div>
        <div className="uppercase text-center font-primary text-s font-medium leading-4 tracking-widest text-black p-xs mb-m">
          {t("label.shortlist.count")} ({favorites.length || 0})
        </div>
        {/* Personal area code comment until required registration process, uncomment when need registration */}
        {/* {!isLogged && (
          <div className="font-secondary font-thin text-s text-center pb-m">
            {t("label.looseProgress")}
            <br />
            <span className="cursor-pointer underline" onClick={handleRegister}>
              {t("label.register")}
            </span>{" "}
            {t("label.shortlist.recoverUnits")}
          </div>
        )} */}
        {favorites.length > 0 && (
          <>
            {/*<div className="max-h-5xl md:max-h-[430px] overflow-auto no-scrollbar"> Bug 78930 issue scroll with 4 or more units */}
            {/* <div className="max-h-5xl md:max-h-[430px] overflow-auto"> BUG 87147 */}
            <div className="max-h-5xl md:max-h-[min(calc(100vh-300px),430px)] overflow-auto">
              {favorites.map((item, index) => (
                <AvailableUnitListProvider
                  key={item.id}
                  category={item.type === PRODUCT_TYPE.PROPERTY_TYPE ? item.assignedCategory : undefined}
                  locale={toUnicodeLocale(locale)}
                >
                  <Card item={item} index={index} />
                </AvailableUnitListProvider>
              ))}
            </div>
            <div className="pt-m">
              {favorites.length > 2 && (
                <div className="absolute bottom-0 left-0 right-0 h-5xl backdrop-cta opacity-80 pointer-events-none z-0 w-full" />
              )}
              <Button
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.BLACK}
                customClass={`uppercase w-full ${favorites.length > 2 && "relative z-10"}`}
                type="button"
                variant={ButtonVariant.PRIMARY}
                onClick={() => {
                  const body: any = {
                    cta_name: t("label.shortlist.viewAndCompare"),
                    user_logged_in: session?.user ? true : false,
                    // user_id: session?.user.id,
                    items: [],
                  };
                  favorites?.forEach((item) => {
                    const itemObj = {
                      currency: item.currency,
                      property_name: item.propertyName || item.projectName || "",
                      project_location: item.location,
                      project_type: item.identifier || item.propertyType,
                      bedroom_numbers: item.bedrooms,
                      unit_type: item.isFurnished ? "furnished" : "unfurnished",
                      unit_size: Math.round(item.areaSqft ?? 0) || item.areaMin,
                      unit_price: item.price || Math.round(Number(item.priceMin) / 100),
                      posession_time: getQuarter(item.deliveryDate, locale, t),
                    };
                    body.items.push(itemObj);
                  });
                  ecomViewWishlist(body);
                  router.push(`/${locale}/shortlist`, { scroll: true });
                }}
              >
                {t("label.shortlist.viewAndCompare")}
              </Button>
            </div>
          </>
        )}

        {items.length == 0 && (
          <>
            <div></div>
          </>
        )}
      </div>
    </>
  );
};
export default ShortlistPopup;

const Card: FC<{ item: CommercetoolsProject; index: number }> = ({ item, index }) => {
  const t = useTranslations();
  const { data: session } = useSession();
  const { deleteFavorites } = useNotification();
  const { currency } = useContext(UserContext);
  const format = useFormatter();
  const { setCard } = useContext(SelectUnitContext);
  const { setDesktopPanelID } = useContext(NavigationContext);
  const { loading } = useContext(LoadingContext);
  const { filteredResults } = useContext(AvailableUnitContext);
  const router = useRouter();
  const locale = useLocale();
  const { showUnavailableUnit } = useNotification();
  const handleDelete = (item: CommercetoolsProject) => {
    deleteFavorites([item]);
  };
  const handleSelectUnit = async () => {
    // ctaClickTracking("Reserve now", "header", "SavedUnits");
    setCard({ unitType: item.propertyType[0], categoryID: item.assignedCategory });
    setDesktopPanelID("select-unit");
  };
  const handleReserve = async () => {
    loading(true);
    const unit = item.type === "unit-type" ? item : filteredResults[0];
    const response = await getAvailability(unit.inventoryID, unit.id);
    if (response) {
      const projectName = item.propertyName?.en?.toLowerCase();
      const productIdentifier = `${projectName ?? ""}/${item.slug ?? ""}`;

      ecomInitiateCheckout({
        cta_name: t("label.reserve"),
        form_name: "online_reservation",
        property_name: projectName as string,
        project_location: `${item.area}`,
        // property_type: item.propertyType,
        bedroom_numbers: Array.isArray(item.bedrooms) ? item.bedrooms[0] : item?.bedrooms ?? "",
        bathrooms: "no of bathrooms given", // TODO fixme when data will come
        // unit_id: unit.name,
        product_identifier: productIdentifier,
        // unit_type: unit.unitType,
        unit_category: item.isFurnished ? "furnished" : "unfurnished",
        unit_size: item.areaMax,
        unit_price: item.price || "",
        currency: item.currency,
        handover_qaurter_year: getQuarter(item.deliveryDate, locale, t),
        status: item.status || "",
        user_logged_in: !!session,
        // user_id: session?.user.id || "",
      });
      // router.push(`/${locale}/checkout`, { scroll: true });
      router.push(`/${locale}/checkout?step=contact`, { scroll: true });
    } else {
      loading(false);
      ctaClickTracking("reserve", "header", "property unavailable");
      showUnavailableUnit();
      // showNotification(t("reservation.label.unitUnavailable"), 5000, NotificationType.ERROR);
    }
  };
  const formatValue = (value: string | number): string | number => {
    if (typeof value === "number") {
      return value;
    }

    const cleaned = value.replace(/&/g, "").trim();
    const words = cleaned.split(/\s+/);

    return words.length === 1 ? cleaned : words.join("-");
  };

  return (
    <>
      <div className={`rounded-t-lg bg-white p-s border-b border-gray-10 ${index == 0 ? "" : "mt-m"}`}>
        <div className="flex flex-row relative items-start">
          <button className="absolute right-0" onClick={() => handleDelete(item)}>
            <Icon size={"20"} name={"trash"} className="stroke-black" />
          </button>
          <div>
            <Image src={item?.gallery?.[0] ?? t("noImage")} width={72} height={72} className="min-w-[72px] h-[72px] rounded-md aspect-1-1" alt={""} />
          </div>
          <div className="px-s text-s py-xxs grow">
            <div>
              {/* <span className="font-medium">{item.name}</span> */}
              <span className="font-medium">
                {item.bedrooms}
                {t(`filter.label.propertyType-${formatValue(item?.propertyType?.[0] || item?.identifier || "") as string}`)}
              </span>
            </div>
            <div className="my-s">{[item.project, item.area, item.city].join(", ")}</div>
            <div className="flex flex-row justify-between">
              <div>{item.type === "unit-type" ? item.name : " "}</div>
              <div className="font-medium">
                {/* {format.number(item.type === "unit-type" ? Number(item.price) : Number(item.priceMin) / 100 || 0, {
                  style: "currency",
                  currency: currency,
                  trailingZeroDisplay: "stripIfInteger",
                  maximumFractionDigits: 0,
                })} */}
                {(() => {
                  const formattedPrice = currencyFormatter(
                    item.type === "unit-type" ? Number(item.price) : Number(item.priceMin),
                    currency,
                    format,
                    item.type === "unit-type" ? false : true,
                  );

                  format.number(item.type === "unit-type" ? Number(item.price) : Number(item.priceMin) / 100 || 0, {
                    style: "currency",
                    currency: currency,
                    trailingZeroDisplay: "stripIfInteger",
                    maximumFractionDigits: 0,
                  });

                  if (typeof formattedPrice === "string" && locale?.split("-")?.[0] === "ar" && formattedPrice.includes("د.إ.")) {
                    return (
                      <>
                        {formattedPrice.replace(/AED|د\.إ\./g, "").trim()}
                        <CurrencyIcon color="black" />
                      </>
                    );
                  } else if (formattedPrice.includes("AED")) {
                    return (
                      <>
                        <CurrencyIcon color="black" />
                        {formattedPrice.replace(/AED|د\.إ\./g, "").trim()}
                      </>
                    );
                  } else {
                    return formattedPrice;
                  }
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* {index === 0 && (
                    <div className="rounded-b-lg px-s bg-white flex flex-row justify-between py-m">
                      <div className="underline font-secondary font-medium text-s">Select a specific unit now</div>
                      <div>
                        <Icon size={"16"} name={"chevron-right"} className="stroke-black" />
                      </div>
                    </div>
                  )}
                  {index === 1 && (
                    <div className="rounded-b-lg px-s bg-white flex flex-row justify-between py-m">
                      <div className="underline font-secondary font-medium text-s">Reserve now</div>
                      <div>
                        <Icon size={"16"} name={"chevron-right"} className="stroke-black" />
                      </div>
                    </div>
                  )} */}
      <div
        className="rounded-b-lg px-s bg-white flex flex-row justify-between py-m cursor-pointer"
        onClick={() => {
          if (item.type === "unit-type" || filteredResults?.length === 1) {
            handleReserve();
          } else {
            handleSelectUnit();
            ecomSelectReserve({
              cta_name: t("label.selectSpecificUnit"),
              property_name: item?.projectName,
              project_location: `${item?.area} ${item?.location}`,
              property_type: item?.type,
              bedroom_numbers: Array.isArray(item.bedrooms) ? item.bedrooms[0] : item.bedrooms ?? "",
              bathrooms: Array.isArray(item.bathrooms) && item.bathrooms[0],
              // product_identifier: productIdentifier,
              unit_type: item.propertyType[0] || "",
              unit_category: item.isFurnished ? "furnished" : "unfurnished",
              unit_size: item.slug || "",
              unit_price: Math.round(Number(item.priceMin) / 100),
              currency: item.currency,
              // handover_qaurter_year: getQuarter(entry.projectData.deliveryDate),
              status: item.status || "",
              user_logged_in: !!session,
              // user_id: session?.user.id || "",
            });
          }
        }}
      >
        <div className="underline font-secondary font-medium text-s">
          {t(item.type === "unit-type" || filteredResults?.length === 1 ? "label.reserve" : "label.selectSpecificUnit")}
        </div>
        <div>
          <Icon size={"16"} name={"chevron-right"} className="stroke-black" />
        </div>
      </div>
      {/* {index > 1 && (
                    <div className="rounded-b-lg px-s bg-white flex flex-row justify-between py-m">
                      <div className="underline font-secondary font-medium text-s">Call us for more info</div>
                      <div>
                        <Icon size={"16"} name={"phone"} className="stroke-black" />
                      </div>
                    </div>
                  )} */}
    </>
  );
};
